import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, Inject, Input, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';

import { Subscription } from 'rxjs';

import { AuthHttpService } from '../../services/auth-http.service';
import { AuthService } from '../../services/auth.service';
import { Credentials } from '../../interfaces/credentials';
import { GeolocationService } from '../../../../services/geolocation.service';
import { LocalStorageService } from '../../../../services/local-storage.service';
import { SessionInfo } from '../../interfaces/session-info';
import { WINDOW } from '@shared/services/window.service';

import { environment } from 'environments/environment';

// Icons
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'shared-sign-in-customer-form',
  templateUrl: './sign-in-customer-form.component.html',
  styleUrls: ['./sign-in-customer-form.component.scss'],
  standalone: false
})
export class SignInCustomerFormComponent {

  @Input() mode: string | null = null;

  environment = environment;

  credentials: Credentials = {
    AccountName: null,
    AuthProvider: 'CWK',
    Country: null,
    Email: '',
    FirstName: null,
    IsActive: false,
    LastName: null,
    Password: '',
    Timezone: null,
    Token: null
  };
  rememberEmail: boolean;
  status = '';

  // Icons
  faSpinner = faSpinner;

  // Progress flags
  errorResendingVerification = '';
  isResendingVerification = false;
  isVerificationResent = false;

  private emailKey = 'LOGIN_EMAIL';

  // Subscriptions
  private geolocationSubscription: Subscription;

  constructor(
    private authHttpService: AuthHttpService,
    private authService: AuthService,
    @Inject(DOCUMENT) private document: Document,
    private geolocationService: GeolocationService,
    private localStorageService: LocalStorageService,
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
    @Inject(WINDOW) private window: Window | null
  ) {

    // If email is stored, set it as default
    const storedEmail = this.localStorageService.getItem(this.emailKey);

    // Prepopulate credentials if stored
    if (storedEmail) {
      this.credentials.Email = storedEmail;
    }

    // If stored, it was remembered
    this.rememberEmail = !!storedEmail;

    // Get current geolocation info
    this.geolocationSubscription = this.geolocationService.getObservable()
      .subscribe((value) => {
        this.credentials.Country = value.country_code;
        if (this.credentials.Country === 'US') {
          this.credentials.State = value.region_code;
        }
      });
  }

  cancel() {

    // Sign out
    this.authService.signOut().subscribe(() => {});

    // If the mode is set, go back to homepage
    if (this.mode) {
      this.window.location.replace('/');
    } else {
      history.back();
    }
  }

  // Unauthenticated user wants to create new account
  createAccount() {
    if (this.window) {
      this.window.location.href = environment.urlRoot + '/create-account' + this.window.location.search;
    }
  }

  goResetPassword() {
    if (this.window) {
      this.window.location.href = environment.urlRoot + '/reset' + this.window.location.search;
    }
  }

  resendVerificationEmail() {

    // Immediately leave if we're already resending
    if (this.isResendingVerification) {
      return;
    }

    // Show spinner
    this.isResendingVerification = true;

    // Reset the status
    this.errorResendingVerification = '';
    this.isVerificationResent = false;

    // Call the web service
    this.authHttpService.resendVerificationEmail(this.credentials.Email)
      .subscribe(
        () => {

          // Show message
          this.isVerificationResent = true;

          // Hide spinner
          this.isResendingVerification = false;
        },
        (error) => {

          // Set the error
          this.errorResendingVerification = error.error;

          // Hide spinner
          this.isResendingVerification = false;
        }
      );
  }

  submit() {

    // Store / delete email from local storage
    if (this.rememberEmail) {
      this.localStorageService.setItem(this.emailKey, this.credentials.Email);
    } else {
      this.localStorageService.removeItem(this.emailKey);
    }

    // Set status to wait
    this.status = 'WAIT';

    // If timezone is resolvable, pass it with credentials
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      this.credentials.Timezone = timezone;
    }

    // Send credentials to backend
    return this.authHttpService.signIn(this.credentials)
      .subscribe(
        (response: string) => {
          this.authService.signInSuccess(JSON.parse(response) as SessionInfo);

          // Go back and refresh
          if (this.window && isPlatformBrowser(this.platformId)) {
            const previousUrl = this.document.referrer;

            if (previousUrl) {

              // Check if the referrer is within our application's domain
              const isSameOrigin = previousUrl.startsWith(this.window.location.origin);

              if (isSameOrigin) {
                let relativePath = previousUrl.replace(this.window.location.origin, '');

                // If the previous page is sign-in page, go to the homepage
                if (relativePath.startsWith('/sign-in')) {
                  relativePath = '/';
                }

                this.window.location.replace(relativePath);
                /*this.router.navigateByUrl(relativePath, { replaceUrl: true })
                  .then(() => this.window.location.reload());*/
              } else {

                // If the referrer is from a different origin, navigate to homepage
                this.window.location.replace('/');
                /*this.router.navigateByUrl('/', { replaceUrl: true })
                  .then(() => this.window.location.reload());*/
              }
            } else {

              // No referrer, navigate to homepage
              this.window.location.replace('/');
              /*this.router.navigateByUrl('/', { replaceUrl: true })
                .then(() => this.window.location.reload());*/
            }
          }
        },
        error => this.status = error.error
      );
  }
}
